import { IconType } from 'react-icons';
import {
  FaBan,
  FaCog,
  FaExchangeAlt,
  FaFileAlt,
  FaPlusCircle,
  FaStickyNote,
  FaSyncAlt,
  FaUserShield,
} from 'react-icons/fa';
import type { TimelineEvent, TimelineEventType } from '../types';
import { formatDateTime, relativeTime } from '../utils';

const ICONS: Record<TimelineEventType, IconType> = {
  created: FaPlusCircle,
  status_change: FaExchangeAlt,
  underwriting: FaUserShield,
  document: FaFileAlt,
  note: FaStickyNote,
  renewal: FaSyncAlt,
  cancellation: FaBan,
  system: FaCog,
};

const ACCENT: Record<TimelineEventType, string> = {
  created: 'var(--color-primary-500)',
  status_change: 'var(--color-info-500)',
  underwriting: 'var(--color-secondary-600)',
  document: 'var(--color-primary-600)',
  note: 'var(--color-text-secondary)',
  renewal: 'var(--color-success-500)',
  cancellation: 'var(--color-error-500)',
  system: 'var(--color-text-secondary)',
};

export const Timeline: React.FC<{ events: TimelineEvent[] }> = ({ events }) => {
  if (events.length === 0) {
    return (
      <p className="text-sm" style={{ color: 'var(--color-text-secondary)' }}>
        No activity recorded yet.
      </p>
    );
  }
  return (
    <ol className="relative space-y-6">
      {events.map((event, index) => {
        const Icon = ICONS[event.type];
        const accent = ACCENT[event.type];
        const isLast = index === events.length - 1;
        return (
          <li key={event.id} className="relative flex gap-4">
            {!isLast && (
              <span
                className="absolute left-[15px] top-9 h-[calc(100%+0.25rem)] w-px"
                style={{ backgroundColor: 'var(--color-border-light)' }}
                aria-hidden="true"
              />
            )}
            <span
              className="relative z-10 flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-full"
              style={{ backgroundColor: `color-mix(in srgb, ${accent} 15%, transparent)`, color: accent }}
              aria-hidden="true"
            >
              <Icon className="h-3.5 w-3.5" />
            </span>
            <div className="min-w-0 flex-1 pb-1">
              <div className="flex flex-wrap items-baseline justify-between gap-x-3">
                <p className="text-sm font-semibold" style={{ color: 'var(--color-text-primary)' }}>
                  {event.title}
                </p>
                <time
                  className="text-xs"
                  style={{ color: 'var(--color-text-secondary)' }}
                  title={formatDateTime(event.timestamp)}
                >
                  {relativeTime(event.timestamp)}
                </time>
              </div>
              <p className="mt-0.5 text-sm" style={{ color: 'var(--color-text-secondary)' }}>
                {event.description}
              </p>
              <p className="mt-1 text-xs font-medium" style={{ color: 'var(--color-text-secondary)' }}>
                {event.actor} · {formatDateTime(event.timestamp)}
              </p>
            </div>
          </li>
        );
      })}
    </ol>
  );
};
